/**
 * Axios Configuration Utility
 * 
 * Sets up and configures axios for the Prelude application.
 * Features:
 * - Custom instance with base URL configuration
 * - Request interceptors for authentication
 * - Response interceptors for error handling
 * - Token management for API requests
 * - Automatic error response formatting
 */

import axios from 'axios';
import { toast } from 'react-toastify';

const customFetch = axios.create({
  baseURL: process.env.REACT_APP_API_URL ? `${process.env.REACT_APP_API_URL}/api/v1` : 'http://localhost:8000/api/v1',
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true // Enable sending cookies
});

// Request interceptor
customFetch.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    console.log('🚀 Request:', {
      method: config.method,
      url: config.url,
      baseURL: config.baseURL,
      data: config.data,
      headers: { ...config.headers, Authorization: token ? `Bearer ${token}` : undefined },
    });

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    console.error('❌ Request Error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor
customFetch.interceptors.response.use(
  (response) => {
    console.log('✅ Response:', {
      status: response.status,
      data: response.data,
    });
    return response;
  },
  (error) => {
    console.error('❌ Response Error:', {
      status: error.response?.status,
      data: error.response?.data,
      message: error.message,
      config: error.config,
      isNetworkError: !error.response && !error.request,
      isRequestError: !!error.request && !error.response
    });

    // Handle network errors
    if (!error.response) {
      const message = error.message === 'Network Error' 
        ? 'Unable to connect to the server. Please check your connection.'
        : error.message || 'Network error';
        
      return Promise.reject(new Error(message));
    }

    // Handle other errors
    const message = error.response?.data?.message || error.message || 'An error occurred';
    return Promise.reject(error);
  }
);

export default customFetch;
